import { Chain, ChainPath } from '@ankr.com/chains-list';
import { UserEndpointToken } from 'multirpc-sdk';
import { useCallback, useMemo } from 'react';

import { IFetchProjectsWhitelistsBlockchainsParams } from 'domains/projects/actions/fetchProjectsWhitelistsBlockchains';
import {
  selectAllChainsPaths,
  selectAllPathsByChainId,
  selectAllPathsExceptSubchainsForChainId,
} from 'modules/chains/store/selectors';
import { getUniqueArray } from 'modules/common/utils/getUniqueArray';
import { hasAllBlockchainsInWhitelist } from 'modules/projects/utils/hasAllBlockchainsInWhitelist';
import { selectHasProjectsWithLastChain } from 'domains/projects/store';
import { useAccount } from 'modules/account/hooks/useAccount';
import { useAppSelector } from 'store/useAppSelector';
import { useJWTsManager } from 'domains/jwtToken/hooks/useJWTsManager';
import { useProjectsWhitelistsBlockchains } from 'domains/projects/hooks/useProjectsWhitelistsBlockchains';

import { IProjectSubchains } from './useProjectSubchains';
import { useIsAllIndeterminate } from './useIsAllIndeterminate';
import { useIsAllSelected } from './useIsAllSelected';
import { useProjectsChainsConfirm } from './useProjectsChainsConfirm';

interface IUseChainProjectsSidebarProps {
  chain: Chain;
  onCloseAddToProjectsSidebar: () => void;
  selectedSubchains: IProjectSubchains;
  setSelectedSubchains: (
    subchains: ChainPath[],
    projectToken: UserEndpointToken,
  ) => void;
  setExpandedId?: (expandedId: UserEndpointToken) => void;
}

// eslint-disable-next-line max-lines-per-function
export const useChainProjectsSidebar = ({
  chain,
  onCloseAddToProjectsSidebar,
  selectedSubchains,
  setExpandedId,
  setSelectedSubchains,
}: IUseChainProjectsSidebarProps) => {
  const { group } = useAccount();

  const { jwts: projects } = useJWTsManager();
  const { projectsWhitelistsBlockchains } = useProjectsWhitelistsBlockchains({
    projects,
    group,
    skipFetching: true,
  });

  const allChainPaths = useAppSelector(selectAllChainsPaths);

  const allCurrentChainPaths = useAppSelector(state =>
    selectAllPathsByChainId(state, chain.id),
  );

  const allPathsExceptCurrentChain = useAppSelector(state =>
    selectAllPathsExceptSubchainsForChainId(state, chain.id),
  );

  const handleSelectAll = useCallback(() => {
    projects.forEach(project => {
      const blockchains =
        projectsWhitelistsBlockchains.find(
          ({ userEndpointToken }) =>
            userEndpointToken === project.userEndpointToken,
        )?.blockchains ?? [];

      const projectBlockchains = hasAllBlockchainsInWhitelist(blockchains)
        ? allChainPaths
        : blockchains;

      const newSelectedSubchains = getUniqueArray([
        ...(projectBlockchains || allPathsExceptCurrentChain),
        ...allCurrentChainPaths,
      ]);

      setSelectedSubchains(newSelectedSubchains, project.userEndpointToken);
    });
  }, [
    allChainPaths,
    allCurrentChainPaths,
    allPathsExceptCurrentChain,
    projects,
    projectsWhitelistsBlockchains,
    setSelectedSubchains,
  ]);

  const handleUnselectAll = useCallback(() => {
    projects.forEach(project => {
      const blockchains =
        projectsWhitelistsBlockchains.find(
          ({ userEndpointToken }) =>
            userEndpointToken === project.userEndpointToken,
        )?.blockchains ?? [];

      const projectBlockchains = hasAllBlockchainsInWhitelist(blockchains)
        ? allChainPaths
        : blockchains;

      const newSelectedSubchains =
        projectBlockchains?.filter(
          chainPath => !allCurrentChainPaths?.includes(chainPath),
        ) || allPathsExceptCurrentChain;

      setSelectedSubchains(newSelectedSubchains, project.userEndpointToken);
    });
  }, [
    allChainPaths,
    allCurrentChainPaths,
    allPathsExceptCurrentChain,
    projects,
    projectsWhitelistsBlockchains,
    setSelectedSubchains,
  ]);

  const handleProjectChange = useCallback(
    (projectToken: UserEndpointToken) => {
      const currentProjectSelectedChains = selectedSubchains[projectToken];
      const areAllSubchainsSelected = allCurrentChainPaths?.every(path =>
        currentProjectSelectedChains.includes(path),
      );

      if (areAllSubchainsSelected) {
        const newSelectedSubchains = currentProjectSelectedChains.filter(
          chainPath => !allCurrentChainPaths?.includes(chainPath),
        );

        setSelectedSubchains(newSelectedSubchains, projectToken);
      } else {
        const newSelectedSubchains = getUniqueArray([
          ...currentProjectSelectedChains,
          ...allCurrentChainPaths,
        ]);

        setExpandedId?.(projectToken);
        setSelectedSubchains(newSelectedSubchains, projectToken);
      }
    },
    [
      allCurrentChainPaths,
      selectedSubchains,
      setExpandedId,
      setSelectedSubchains,
    ],
  );

  const {
    isLoadingAddChainsToProject,
    isLoadingAddChainsToProjects,
    onConfirm,
  } = useProjectsChainsConfirm({
    allCurrentChainPaths,
    chain,
    onCloseAddToProjectsSidebar,
    selectedSubchains,
  });

  const isAllSelected = useIsAllSelected({
    allCurrentChainPaths,
    isLoadingAddChainsToProjects,
    projects,
    selectedSubchains,
  });

  const handleAllChange = useCallback(() => {
    if (isAllSelected) {
      handleUnselectAll();
    } else {
      handleSelectAll();
    }
  }, [isAllSelected, handleSelectAll, handleUnselectAll]);

  const isAllIndeterminate = useIsAllIndeterminate({
    allCurrentChainPaths,
    isAllSelected,
    isLoadingAddChainsToProjects,
    projects,
    selectedSubchains,
  });

  const projectsWhitelistsBlockchainParams = useMemo(
    (): IFetchProjectsWhitelistsBlockchainsParams => ({
      group,
      projects,
    }),
    [group, projects],
  );

  const hasDisabledChildren = useAppSelector(state =>
    selectHasProjectsWithLastChain(
      state,
      projectsWhitelistsBlockchainParams,
      chain.id,
    ),
  );

  return {
    handleAllChange,
    handleProjectChange,
    hasDisabledChildren,
    isAllIndeterminate,
    isAllSelected,
    isLoadingAddChainsToProject,
    isLoadingAddChainsToProjects,
    onConfirm,
  };
};
